import { useState, useEffect } from "react"
import { Container, Box } from "@mui/material"
import Header from "../components/Header"
import CategoryFilter from "../components/CategoryFilter"
import ProductGrid from "../components/ProductGrid"
import CustomOrderButton from "../components/CustomOrderButton"
import CustomOrderModal from "../components/CustomOrderModal"
import InfoModal from "../components/InfoModal"
import ProductDetailModal from "../components/ProductDetailModal"
import type { Product } from "../types"
import productsData from "../data/products.json"
import categoriesData from "../data/categories.json"
import Footer from "../components/Footer"

// Función para normalizar texto (eliminar acentos y convertir a minúsculas)
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

// Función para verificar si un producto coincide con el término de búsqueda
const productMatchesSearch = (product: Product, normalizedSearchTerm: string): boolean => {
  if (!normalizedSearchTerm) return true
  const searchWords = normalizedSearchTerm.split(/\s+/).filter((word) => word.length > 0)
  if (searchWords.length === 0) return true

  // Normaliza los campos del producto para la búsqueda
  const normalizedName = normalizeText(product.name)
  const normalizedDescription = normalizeText(product.description)
  const normalizedCategory = product.category.map((cat) => normalizeText(cat))
  const normalizedSize = normalizeText(product.size)
  const normalizedTags = product.tags.map((tag) => normalizeText(tag))

  // Verifica si todas las palabras de búsqueda están presentes en algún campo
  return searchWords.every((word) => {
    return (
      normalizedName.includes(word) ||
      normalizedDescription.includes(word) ||
      normalizedCategory.some((cat) => cat.includes(word)) ||
      normalizedSize.includes(word) ||
      normalizedTags.some((tag) => tag.includes(word))
    )
  })
}

const HomePage = () => {
  const typedProductsData = productsData as Product[]

  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [categories] = useState(categoriesData)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [showInStockOnly, setShowInStockOnly] = useState(false)
  const [showCustomOrderModal, setShowCustomOrderModal] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  useEffect(() => {
    // Filtrar productos que estén habilitados y tengan cantidad > 0
    const availableProducts = typedProductsData.filter(
      (product) => product.enabled && product.cantidad > 0
    )
    setProducts(availableProducts)
    setFilteredProducts(availableProducts)
  }, [typedProductsData])

  useEffect(() => {
    let result = products

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const normalizedSearchTerm = normalizeText(searchTerm)
      result = result.filter((product) =>
        productMatchesSearch(product, normalizedSearchTerm)
      )
    }

    // Filtrar por categoría
    if (selectedCategory !== "all") {
      result = result.filter((product) =>
        product.category.includes(selectedCategory)
      )
    }

    // Filtrar por stock
    if (showInStockOnly) {
      result = result.filter((product) => product.status === "ready")
    }

    setFilteredProducts(result)
  }, [searchTerm, selectedCategory, showInStockOnly, products])

  // Handle back button for modals
  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      // If any modal is open, prevent the default back action and close the modal instead
      if (selectedProduct || showCustomOrderModal || showInfoModal) {
        // Prevent the default back action
        event.preventDefault()
        window.history.pushState(null, "", window.location.pathname)

        // Close the appropriate modal
        if (selectedProduct) {
          setSelectedProduct(null)
        } else if (showCustomOrderModal) {
          setShowCustomOrderModal(false)
        } else if (showInfoModal) {
          setShowInfoModal(false)
        }
      }
    }


    // Add state to history when a modal is opened
    const addHistoryState = () => {
      if (selectedProduct || showCustomOrderModal || showInfoModal) {
        window.history.pushState(null, "", window.location.pathname)
      }
    }

    // Listen for popstate events (back button)
    window.addEventListener("popstate", handleBackButton)

    // Add history state when modals change
    addHistoryState()

    return () => {
      window.removeEventListener("popstate", handleBackButton)
    }
  }, [selectedProduct, showCustomOrderModal, showInfoModal])

  const handleSearch = (term: string) => {
    setSearchTerm(term)
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
  }

  const handleInStockChange = (checked: boolean) => {
    setShowInStockOnly(checked)
  }

  const openProductDetail = (product: Product) => {
    setSelectedProduct(product)
  }

  const closeProductDetail = () => {
    setSelectedProduct(null)
  }

  const handleTagClick = (tag: string) => {
    setSearchTerm(tag)
  }



  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Header y Filtros Fijos */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          width: '100%',
        }}
      >
        <Header
          logoUrl="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-NEklmR3dJgSvNjmf6kh91GoRFr97d5.png"
          onSearch={handleSearch}
          searchTerm={searchTerm}
        />

        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          showInStockOnly={showInStockOnly}
          onInStockChange={handleInStockChange}
        />
      </Box>

      {/* Contenido con Scroll */}
      <Box
        component="main"
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          pt: 2,
          '& > *': {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
        }}
      >
        <Container maxWidth="lg">
          <ProductGrid
            products={filteredProducts}
            onProductClick={openProductDetail}
            onInfoClick={() => setShowInfoModal(true)}
          />
        </Container>
        <Footer />
      </Box>

      <CustomOrderButton onClick={() => setShowCustomOrderModal(true)} />

      {showCustomOrderModal && (
        <CustomOrderModal onClose={() => setShowCustomOrderModal(false)} />
      )}

      {showInfoModal && <InfoModal onClose={() => setShowInfoModal(false)} />}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={closeProductDetail}
          onInfoClick={() => setShowInfoModal(true)}
          onTagClick={handleTagClick}
        />
      )}
    </Box>
  )
}

export default HomePage
